/**
 * ===========================================================================
 *  LA FACTURE CLIENT EN PDF (10/10/2026)
 * ===========================================================================
 *
 *  Les clients demandent une facture de leur commande. Ce fichier fabrique
 *  le PDF directement dans le navigateur, sans bibliotheque a installer et
 *  sans rien envoyer a un serveur : le bouton « Facture PDF » du detail de
 *  la commande appelle telechargerFacture(), et le fichier se telecharge.
 *
 *  CE QUI APPARAIT : le vendeur, le client, les articles au PRIX DE VENTE,
 *  la livraison et le total paye. JAMAIS les prix d'achat, les fournisseurs
 *  ni les marges.
 *
 *  LES MENTIONS LEGALES (ICE, RC, IF, adresse du siege...) se remplissent
 *  dans VENDEUR ci-dessous. Une ligne vide n'est pas imprimee.
 *
 *  Technique : PDF 1.4 ecrit a la main, polices standard Helvetica (aucune
 *  police a embarquer), encodage WinAnsi — les accents francais passent ;
 *  un caractere que la police ne connait pas (arabe, emoji) devient « ? ».
 * ===========================================================================
 */

/** A COMPLETER avec les informations legales de la societe. */
// Source : extrait du registre de commerce (Tribunal de commerce de
// Casablanca, RC 553439), fourni le 10/10/2026. IF et telephone : a ajouter
// quand on les aura — une ligne vide n'est pas imprimee.
export const VENDEUR = {
  nom: "Natura Beldi",
  raisonSociale: "NATURA BELDI — SARL AU au capital de 100 000,00 MAD",
  adresse: "Rue Regraga, Résidence La Corniche, Imm. 2, RDC, Apt 1, Bourgogne, Casablanca",
  telephone: "",
  email: "",
  ice: "003134604000035",
  rc: "553439 (Casablanca)",
  identifiantFiscal: "",
};

export interface LigneFacture {
  designation: string;
  quantite: number;
  /** Prix unitaire TTC, en centimes. */
  prixUnitaireCentimes: number;
}

export interface DonneesFacture {
  numero: string;
  date: Date;
  magasin?: string;
  origine?: string;
  paiement?: string;
  client: {
    nom?: string;
    telephone?: string;
    email?: string;
    adresse?: string;
  };
  lignes: LigneFacture[];
  livraisonCentimes: number;
  /** Ecart entre le total paye et articles + livraison (remise, frais...). */
  ajustementCentimes: number;
  totalCentimes: number;
}

/* ------------------------------------------------------------------------ */
/*  Polices et mesure du texte                                              */
/* ------------------------------------------------------------------------ */

// Largeurs Helvetica / Helvetica-Bold (AFM Adobe), caracteres 32 a 126.
const L_NORMAL = [
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278,
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556,
  1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778,
  667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556,
  333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556,
  556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584,
];
const L_GRAS = [
  278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278,
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 333, 333, 584, 584, 584, 611,
  975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778,
  667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556,
  333, 556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611,
  611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584,
];

function largeurCaractere(c: string, gras: boolean): number {
  const table = gras ? L_GRAS : L_NORMAL;
  let code = c.charCodeAt(0);
  if (code < 32 || code > 126) {
    // Lettre accentuee : meme largeur que la lettre de base.
    const base = c.normalize("NFD").charAt(0);
    code = base.charCodeAt(0);
    if (code < 32 || code > 126) return 556;
  }
  return table[code - 32];
}

export function largeur(texte: string, taille: number, gras = false): number {
  let total = 0;
  for (const c of texte) total += largeurCaractere(c, gras);
  return (total * taille) / 1000;
}

/** Coupe un texte en lignes qui tiennent dans `max` points. */
export function couper(texte: string, max: number, taille: number, gras = false): string[] {
  const mots = String(texte || "").replace(/\s+/g, " ").trim().split(" ");
  const lignes: string[] = [];
  let courante = "";
  for (const mot of mots) {
    const essai = courante ? `${courante} ${mot}` : mot;
    if (largeur(essai, taille, gras) <= max) {
      courante = essai;
      continue;
    }
    if (courante) lignes.push(courante);
    // Un mot plus long que la colonne : on le coupe net.
    let reste = mot;
    while (largeur(reste, taille, gras) > max && reste.length > 1) {
      let n = reste.length;
      while (n > 1 && largeur(reste.slice(0, n), taille, gras) > max) n--;
      lignes.push(reste.slice(0, n));
      reste = reste.slice(n);
    }
    courante = reste;
  }
  if (courante) lignes.push(courante);
  return lignes.length ? lignes : [""];
}

/* ------------------------------------------------------------------------ */
/*  Encodage WinAnsi et chaines PDF                                         */
/* ------------------------------------------------------------------------ */

const WINANSI_SPECIAUX: Record<string, number> = {
  "€": 0x80, "‚": 0x82, "ƒ": 0x83, "„": 0x84, "…": 0x85, "†": 0x86, "‡": 0x87,
  "ˆ": 0x88, "‰": 0x89, "Š": 0x8a, "‹": 0x8b, "Œ": 0x8c, "Ž": 0x8e, "‘": 0x91,
  "’": 0x92, "“": 0x93, "”": 0x94, "•": 0x95, "–": 0x96, "—": 0x97, "˜": 0x98,
  "™": 0x99, "š": 0x9a, "›": 0x9b, "œ": 0x9c, "ž": 0x9e, "Ÿ": 0x9f,
};

/** Texte -> litteral PDF « (...) », 100 % ASCII (octal au-dela de 126). */
function chainePdf(texte: string): string {
  let sortie = "(";
  for (const c of String(texte ?? "")) {
    let code = c.charCodeAt(0);
    if (c === " ") code = 32; // espace insecable (separateur de milliers)
    else if (WINANSI_SPECIAUX[c] !== undefined) code = WINANSI_SPECIAUX[c];
    else if (code > 255 || (code > 126 && code < 160)) code = 63; // « ? »
    if (code === 40 || code === 41 || code === 92) sortie += "\\" + String.fromCharCode(code);
    else if (code < 32) sortie += " ";
    else if (code > 126) sortie += "\\" + code.toString(8).padStart(3, "0");
    else sortie += String.fromCharCode(code);
  }
  return sortie + ")";
}

/* ------------------------------------------------------------------------ */
/*  Mise en page                                                            */
/* ------------------------------------------------------------------------ */

export const PAGE_L = 595.28;
export const PAGE_H = 841.89;
export const MARGE = 42;

export function enDirhams(centimes: number): string {
  const v = (Math.round(centimes) / 100).toFixed(2);
  const [e, d] = v.split(".");
  return `${e.replace(/\B(?=(\d{3})+(?!\d))/g, " ")},${d} DH`;
}

function dateFr(d: Date): string {
  const j = String(d.getDate()).padStart(2, "0");
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${j}/${m}/${d.getFullYear()}`;
}

export class Page {
  ops: string[] = [];
  texte(x: number, y: number, t: string, taille = 10, gras = false, gris = false) {
    this.ops.push(
      `BT ${gris ? "0.42 0.45 0.5 rg" : "0.1 0.1 0.12 rg"} /${gras ? "F2" : "F1"} ${taille} Tf ${x.toFixed(2)} ${y.toFixed(2)} Td ${chainePdf(t)} Tj ET`,
    );
  }
  droite(xDroite: number, y: number, t: string, taille = 10, gras = false) {
    this.texte(xDroite - largeur(t, taille, gras), y, t, taille, gras);
  }
  rectangle(x: number, y: number, l: number, h: number, gris = 0.95) {
    this.ops.push(`${gris} ${gris} ${gris} rg ${x} ${y} ${l} ${h} re f`);
  }
  trait(x1: number, y1: number, x2: number, y2: number, epaisseur = 0.6) {
    this.ops.push(`0.8 0.82 0.85 RG ${epaisseur} w ${x1} ${y1} m ${x2} ${y2} l S`);
  }
}

function construirePages(f: DonneesFacture): Page[] {
  const pages: Page[] = [];
  let p = new Page();
  pages.push(p);
  let y = PAGE_H - MARGE;

  // --- En-tete ------------------------------------------------------------
  p.texte(MARGE, y - 18, "FACTURE", 22, true);
  p.droite(PAGE_L - MARGE, y - 6, `N° ${f.numero}`, 11, true);
  p.droite(PAGE_L - MARGE, y - 22, `Date : ${dateFr(f.date)}`, 10);
  y -= 50;

  // --- Vendeur et client, cote a cote --------------------------------------
  const colG = MARGE;
  const colD = PAGE_L / 2 + 10;
  const largeurBloc = PAGE_L / 2 - MARGE - 10;

  const vendeur: string[] = [];
  if (VENDEUR.raisonSociale) vendeur.push(...couper(VENDEUR.raisonSociale, largeurBloc, 9));
  if (f.magasin) vendeur.push(`Magasin : ${f.magasin}`);
  if (VENDEUR.adresse) vendeur.push(...couper(VENDEUR.adresse, largeurBloc, 9));
  if (VENDEUR.telephone) vendeur.push(`Tél. : ${VENDEUR.telephone}`);
  if (VENDEUR.email) vendeur.push(VENDEUR.email);
  if (VENDEUR.ice) vendeur.push(`ICE : ${VENDEUR.ice}`);
  if (VENDEUR.rc) vendeur.push(`RC : ${VENDEUR.rc}`);
  if (VENDEUR.identifiantFiscal) vendeur.push(`IF : ${VENDEUR.identifiantFiscal}`);

  const client: string[] = [];
  if (f.client.nom) client.push(...couper(f.client.nom, largeurBloc, 9));
  if (f.client.telephone) client.push(`Tél. : ${f.client.telephone}`);
  if (f.client.email) client.push(...couper(f.client.email, largeurBloc, 9));
  if (f.client.adresse) client.push(...couper(f.client.adresse, largeurBloc, 9));
  if (client.length === 0) client.push("—");

  p.texte(colG, y, VENDEUR.nom, 12, true);
  p.texte(colD, y, "Client", 12, true);
  let yG = y - 15;
  let yD = y - 15;
  for (const l of vendeur) { p.texte(colG, yG, l, 9); yG -= 12; }
  for (const l of client) { p.texte(colD, yD, l, 9); yD -= 12; }
  y = Math.min(yG, yD) - 8;

  if (f.origine || f.paiement) {
    const infos = [f.origine, f.paiement].filter(Boolean).join("   ·   ");
    p.texte(MARGE, y, infos, 9, false, true);
    y -= 18;
  }

  // --- Tableau des articles -----------------------------------------------
  const xQte = 360;
  const xPu = 455;
  const xTot = PAGE_L - MARGE;
  const largeurDesignation = xQte - MARGE - 30;

  const enTeteTableau = () => {
    p.rectangle(MARGE, y - 6, PAGE_L - 2 * MARGE, 20, 0.93);
    p.texte(MARGE + 6, y, "Désignation", 9, true);
    p.droite(xQte, y, "Qté", 9, true);
    p.droite(xPu, y, "Prix unitaire", 9, true);
    p.droite(xTot - 6, y, "Total", 9, true);
    y -= 22;
  };
  enTeteTableau();

  for (const ligne of f.lignes) {
    const morceaux = couper(ligne.designation, largeurDesignation, 9);
    const hauteur = morceaux.length * 11 + 8;
    if (y - hauteur < MARGE + 130) {
      p = new Page();
      pages.push(p);
      y = PAGE_H - MARGE - 10;
      enTeteTableau();
    }
    morceaux.forEach((m, i) => p.texte(MARGE + 6, y - i * 11, m, 9));
    p.droite(xQte, y, String(ligne.quantite), 9);
    p.droite(xPu, y, enDirhams(ligne.prixUnitaireCentimes), 9);
    p.droite(xTot - 6, y, enDirhams(ligne.prixUnitaireCentimes * ligne.quantite), 9);
    y -= hauteur - 4;
    p.trait(MARGE, y + 4, PAGE_L - MARGE, y + 4);
    y -= 6;
  }

  // --- Totaux --------------------------------------------------------------
  if (y < MARGE + 120) {
    p = new Page();
    pages.push(p);
    y = PAGE_H - MARGE - 10;
  }
  const sousTotal = f.lignes.reduce((s, l) => s + l.prixUnitaireCentimes * l.quantite, 0);
  y -= 8;
  const ligneTotal = (libelle: string, montant: number, gras = false) => {
    p.droite(xPu, y, libelle, gras ? 11 : 9.5, gras);
    p.droite(xTot - 6, y, enDirhams(montant), gras ? 11 : 9.5, gras);
    y -= gras ? 18 : 14;
  };
  ligneTotal("Sous-total articles", sousTotal);
  if (f.livraisonCentimes) ligneTotal("Livraison", f.livraisonCentimes);
  if (f.ajustementCentimes) {
    ligneTotal(f.ajustementCentimes < 0 ? "Remise" : "Autres frais", f.ajustementCentimes);
  }
  y -= 2;
  p.rectangle(xPu - 120, y - 6, xTot - (xPu - 120), 22, 0.93);
  y += 1;
  ligneTotal("TOTAL TTC", f.totalCentimes, true);

  // --- Pied de page --------------------------------------------------------
  pages.forEach((pg, i) => {
    pg.trait(MARGE, MARGE + 18, PAGE_L - MARGE, MARGE + 18);
    pg.texte(MARGE, MARGE + 4, `${VENDEUR.nom} — merci pour votre confiance. Montants en dirhams (MAD), TTC.`, 8, false, true);
    if (pages.length > 1) pg.droite(PAGE_L - MARGE, MARGE + 4, `Page ${i + 1}/${pages.length}`, 8);
  });

  return pages;
}

/** Le fichier PDF complet, sous forme de texte ASCII. */
export function facturePdfTexte(f: DonneesFacture): string {
  return assemblerPdf(construirePages(f));
}

/** Des pages -> le fichier PDF (texte ASCII). Commun a tous nos PDF. */
export function assemblerPdf(pages: Page[]): string {
  const objets: string[] = [];
  // 1 catalogue, 2 arbre des pages, 3 Helvetica, 4 Helvetica-Bold,
  // puis pour chaque page : l'objet page et son flux.
  const idsPages = pages.map((_, i) => 5 + i * 2);
  objets[1] = "<< /Type /Catalog /Pages 2 0 R >>";
  objets[2] = `<< /Type /Pages /Kids [${idsPages.map((id) => `${id} 0 R`).join(" ")}] /Count ${pages.length} >>`;
  objets[3] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>";
  objets[4] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>";
  pages.forEach((pg, i) => {
    const idPage = 5 + i * 2;
    const idFlux = idPage + 1;
    const flux = pg.ops.join("\n");
    objets[idPage] =
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_L} ${PAGE_H}] ` +
      `/Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${idFlux} 0 R >>`;
    objets[idFlux] = `<< /Length ${flux.length} >>\nstream\n${flux}\nendstream`;
  });

  let pdf = "%PDF-1.4\n";
  const positions: number[] = [];
  for (let id = 1; id < objets.length; id++) {
    positions[id] = pdf.length;
    pdf += `${id} 0 obj\n${objets[id]}\nendobj\n`;
  }
  const debutXref = pdf.length;
  pdf += `xref\n0 ${objets.length}\n0000000000 65535 f \n`;
  for (let id = 1; id < objets.length; id++) {
    pdf += `${String(positions[id]).padStart(10, "0")} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${objets.length} /Root 1 0 R >>\nstartxref\n${debutXref}\n%%EOF\n`;
  return pdf;
}

/** Fabrique la facture et la telecharge dans le navigateur. */
export function telechargerFacture(f: DonneesFacture): void {
  telechargerPdf(facturePdfTexte(f), `facture-${String(f.numero).replace(/[^A-Za-z0-9_-]+/g, "_")}.pdf`);
}

/** Telecharge un PDF (texte ASCII) sous ce nom. */
export function telechargerPdf(texte: string, nomFichier: string): void {
  const octets = new Uint8Array(texte.length);
  for (let i = 0; i < texte.length; i++) octets[i] = texte.charCodeAt(i) & 0xff;
  const blob = new Blob([octets], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const lien = document.createElement("a");
  lien.href = url;
  lien.download = nomFichier;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
